import {useCallback, useEffect, useState} from "react";
import useLogbogApi from "../../../core/hooks/useLogbogApi";
import {
    KursusoverviewFilterAsyncListInput, KursusoverviewFilterModel,
    KursusoverviewModel
} from "../models/KursusoverviewModel";
import {nameof} from "ts-simple-nameof";
import {AsyncListSortOrderInput} from "../../../core/sharedmodels/asyncList/asyncListSortOrderInput";
import {AsyncListInput} from "../../../core/sharedmodels/asyncList/asyncListInput";
import {PaginationModel} from "../../../core/sharedmodels/filter/PaginationModel";
import { HandleError } from "core/infrastructure/errors/errorBoundary";

const filterInitialValue: KursusoverviewFilterModel = {
    kursusgrupper: [],
    regionIds: [],
    specialeIds: [],
    uddannelseIds: [],
}

const commonFilterValues = {
    draw: 0, // (pageindex) is 1, according to our pagination component
    start: 0,
    length: 10,
    search: {
        value: "",
    },
    order: [{
        columnName: nameof<KursusoverviewModel>(x => x.brugerNavn),
        dir: "asc"
    } as AsyncListSortOrderInput],
    grouping: [],
} as AsyncListInput;

const tableFilterInitialValue: KursusoverviewFilterAsyncListInput = {
    kursusoverviewFilter: filterInitialValue,
    ...commonFilterValues
}

export const useKursusOverview = () => {
    const {
        logbogLaegekursusApi,
        logbogExportApi
    } = useLogbogApi();

    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [fetchFailed, setFetchFailed] = useState(false);
    const [kursusoverviewData, setKursusoverviewData] = useState<KursusoverviewModel[]>([])
    const [kursusoverviewAsyncListInput, setKursusoverviewAsyncListInput] = useState<KursusoverviewFilterAsyncListInput>(tableFilterInitialValue)
    const [paginationModel, setPaginationModel] = useState<PaginationModel>();

    useEffect(() => {
        setIsLoading(true);
        setFetchFailed(false);
        const fetchKurser = async () => {
            const kursusoverview = await logbogLaegekursusApi.getKursusoverview(kursusoverviewAsyncListInput);
            setPaginationModel(new PaginationModel(kursusoverviewAsyncListInput.start, kursusoverviewAsyncListInput.length, kursusoverview.recordsTotal, kursusoverview.recordsFiltered));
            setKursusoverviewData(kursusoverview.data);
        }
        fetchKurser()
            .catch(err => {
                setFetchFailed(true);
                setKursusoverviewData([]);
                HandleError(err);
            })
            .finally(() => setIsLoading(false));

        return () => {
            logbogLaegekursusApi.cancelPendingRequests();
        }
    }, [kursusoverviewAsyncListInput, logbogLaegekursusApi])

    const exportExcel = useCallback(async () => {
        await logbogExportApi.getKursusOverviewExcel(kursusoverviewAsyncListInput);
    }, [kursusoverviewAsyncListInput, logbogExportApi]);

    return {
        asyncListInput: kursusoverviewAsyncListInput,
        setAsyncListInput: setKursusoverviewAsyncListInput,
        setFilter: (filter: KursusoverviewFilterModel) => setKursusoverviewAsyncListInput(prev => ({
            ...prev,
            start: 0,
            kursusoverviewFilter: filter
        })),
        clearFilter: () => setKursusoverviewAsyncListInput(prev => ({
            ...prev,
            start: 0,
            kursusoverviewFilter: filterInitialValue
        })),
        exportExcel,
        data: kursusoverviewData,
        paginationModel: paginationModel,
        isLoading: isLoading,
        fetchFailed
    }
}
